import { useState } from 'react';
import DailyRoom from './DailyRoom';
import { Settings } from './Settings';

interface RoomDetailsResponse {
  dailyRoom?: string;
  dailyToken?: string;
  room_url?: string;
  token?: string;
}

interface SettingsState {
  useAuth: boolean;
  apiEndpoint: string;
  authToken: string;
  requestBody?: any;
}

const DEFAULT_API_ENDPOINT = 'https://api.pipecat.daily.co/v1/public/sandra-voice-web/start';

const SandraWithPipecatCloud: React.FC = () => {
  const [roomDetails, setRoomDetails] = useState<RoomDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isJoined, setIsJoined] = useState(false);
  const [settings, setSettings] = useState<SettingsState>({
    useAuth: true,
    apiEndpoint: DEFAULT_API_ENDPOINT,
    authToken: 'pk_6f167e4b-a991-4dc4-a619-e5ce7e3ea089',
    requestBody: {
      createDailyRoom: true,
      dailyRoomProperties: {
        enable_dialout: true
      }
    }
  });

  const handleJoinRoom = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (settings.useAuth) {
        headers['Authorization'] = `Bearer ${settings.authToken}`;
      }
      
      const response = await fetch(settings.apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(settings.requestBody || { createDailyRoom: true })
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle both response formats
      setRoomDetails({
        dailyRoom: data.dailyRoom || data.room_url,
        dailyToken: data.dailyToken || data.token
      });
      
      setIsLoading(false);
      
      // Only auto-join if using default endpoint
      if (settings.apiEndpoint === DEFAULT_API_ENDPOINT) {
        setTimeout(() => {
          setIsJoined(true);
        }, 10000);
      }
      
    } catch (err) {
      console.error('Error fetching room details:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsLoading(false);
    }
  };

  const handleRoomLeave = () => {
    console.log('User left the room - handleRoomLeave called');
    if (isJoined) {
      setIsJoined(false);
      setRoomDetails(null);
    }
  };

  const getDailyRoomUrl = () => {
    if (!roomDetails) return '';
    const roomUrl = roomDetails.dailyRoom || roomDetails.room_url;
    const token = roomDetails.dailyToken || roomDetails.token;
    return `${roomUrl}?t=${token}`;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <Settings onSettingsChange={setSettings} />
        
        {isLoading && <div>Loading room details...</div>}
        
        {error && (
          <div>
            <div>Error: {error}</div>
            <button onClick={handleJoinRoom}>Try Again</button>
          </div>
        )}

        {!isJoined && roomDetails && settings.apiEndpoint !== DEFAULT_API_ENDPOINT && (
          <div className="join-meeting-container">
            <h2>Daily Room Link</h2>
            <div className="mt-4 p-4 bg-white rounded-lg shadow">
              <a 
                href={getDailyRoomUrl()} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Join Daily Room
              </a>
            </div>
          </div>
        )}

        {!isJoined && roomDetails && settings.apiEndpoint === DEFAULT_API_ENDPOINT && (
          <div className="join-meeting-container">
            <h2>Call will begin in a few seconds</h2>
            <div className="countdown-indicator">
              Please wait...
            </div>
          </div>
        )}

        {!isJoined && !roomDetails && (
          <div className="join-meeting-container">
            <button 
              className="join-button"
              onClick={handleJoinRoom}
              disabled={isLoading}
            >
              {isLoading ? '⏳ Connecting...' : 'Call Agent'}
            </button>
          </div>
        )}

        {isJoined && roomDetails && settings.apiEndpoint === DEFAULT_API_ENDPOINT && (
          <DailyRoom 
            url={roomDetails.dailyRoom || roomDetails.room_url || ''}
            token={roomDetails.dailyToken || roomDetails.token || ''}
            onLeave={handleRoomLeave}
          />
        )}
      </div>
    </div>
  );
};

export default SandraWithPipecatCloud; 